import React, { useState, useEffect } from 'react';
import { bankAccountAPI } from '../../services/api';
import './BankAccounts.css';

const BankAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    account_name: '',
    account_number: '',
    ifsc_code: '',
    branch: '',
    bank_name: '',
    is_default: false,
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await bankAccountAPI.getAll();
      if (response.success) {
        setAccounts(response.data);
      }
    } catch (err) {
      alert('Failed to fetch bank accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingAccount) {
        const response = await bankAccountAPI.update(editingAccount.id, formData);
        if (response.success) {
          alert('Bank account updated successfully');
          fetchAccounts();
          resetForm();
        }
      } else {
        const response = await bankAccountAPI.create(formData);
        if (response.success) {
          alert('Bank account created successfully');
          fetchAccounts();
          resetForm();
        }
      }
    } catch (err) {
      alert('Failed to save bank account');
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      account_name: account.account_name,
      account_number: account.account_number,
      ifsc_code: account.ifsc_code,
      branch: account.branch,
      bank_name: account.bank_name || '',
      is_default: account.is_default,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this bank account?')) {
      try {
        const response = await bankAccountAPI.delete(id);
        if (response.success) {
          alert('Bank account deleted successfully');
          fetchAccounts();
        }
      } catch (err) {
        alert('Failed to delete bank account');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      account_name: '',
      account_number: '',
      ifsc_code: '',
      branch: '',
      bank_name: '',
      is_default: false,
    });
    setEditingAccount(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="loading">Loading bank accounts...</div>;
  }

  return (
    <div className="bank-accounts-container">
      <div className="accounts-header">
        <h2>Bank Accounts</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="create-btn"
        >
          {showForm ? 'Cancel' : '+ Add Bank Account'}
        </button>
      </div>

      {showForm && (
        <div className="account-form-card">
          <h3>{editingAccount ? 'Edit' : 'Create'} Bank Account</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-field">
                <label>Account Name *</label>
                <input
                  type="text"
                  name="account_name"
                  value={formData.account_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Primary Account, Business Account"
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  <input
                    type="checkbox"
                    name="is_default"
                    checked={formData.is_default}
                    onChange={handleInputChange}
                  />
                  {' '}Set as Default
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Bank Name</label>
                <input
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  placeholder="e.g., State Bank of India"
                />
              </div>
              <div className="form-field">
                <label>Account Number *</label>
                <input
                  type="text"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>IFSC Code *</label>
                <input
                  type="text"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleInputChange}
                  maxLength="11"
                  placeholder="e.g., SBIN0001234"
                  required
                />
              </div>
              <div className="form-field">
                <label>Branch *</label>
                <input
                  type="text"
                  name="branch"
                  value={formData.branch}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={resetForm} className="cancel-btn">
                Cancel
              </button>
              <button type="submit" className="submit-btn">
                {editingAccount ? 'Update' : 'Create'} Account
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="accounts-grid">
        {accounts.length === 0 ? (
          <div className="empty-state">
            <p>No bank accounts found. Add your first bank account!</p>
          </div>
        ) : (
          accounts.map((account) => (
            <div key={account.id} className="account-card">
              {account.is_default && <span className="default-badge">Default</span>}
              <h3>{account.account_name}</h3>
              <div className="account-details">
                {account.bank_name && <p><strong>{account.bank_name}</strong></p>}
                <p>A/c No: {account.account_number}</p>
                <p>IFSC: {account.ifsc_code}</p>
                <p>Branch: {account.branch}</p>
              </div>
              <div className="account-actions">
                <button onClick={() => handleEdit(account)} className="edit-btn">
                  Edit
                </button>
                <button onClick={() => handleDelete(account.id)} className="delete-btn">
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BankAccounts;
